// Minutes are wall clock in the block's own zone, so end-of-day is 1440 rather
// than 0 and a window never wraps past midnight.

export type CoverageBlockInput = {
  blockId: string;
  teamId: string;
  channelId: string;
  category: string;
  issue: string;
  daysOfWeek: string[];
  startMinute: number;
  endMinute: number;
};

export type CoverageUnit = Omit<CoverageBlockInput, 'daysOfWeek' | 'teamId'> & {
  dayOfWeek: string;
};

export type CoverageConflict = {
  blockId: string;
  otherBlockId: string;
  dayOfWeek: string;
};

export function expandCoverageUnits(blocks: CoverageBlockInput[]): CoverageUnit[] {
  return blocks.flatMap(block =>
    block.daysOfWeek.map(dayOfWeek => ({
      blockId: block.blockId,
      channelId: block.channelId,
      category: block.category,
      issue: block.issue,
      dayOfWeek,
      startMinute: block.startMinute,
      endMinute: block.endMinute,
    }))
  );
}

function issuesAreRelated(a: string, b: string): boolean {
  return a === b;
}

function areComparable(a: CoverageUnit, b: CoverageUnit): boolean {
  return (
    a.channelId === b.channelId &&
    a.category === b.category &&
    a.dayOfWeek === b.dayOfWeek &&
    issuesAreRelated(a.issue, b.issue)
  );
}

export function unitsOverlap(a: CoverageUnit, b: CoverageUnit): boolean {
  if (!areComparable(a, b)) {
    return false;
  }

  return a.startMinute < b.endMinute && b.startMinute < a.endMinute;
}

export function findCoverageConflicts(blocks: CoverageBlockInput[]): CoverageConflict[] {
  const units = expandCoverageUnits(blocks);
  const conflicts: CoverageConflict[] = [];

  units.forEach((unit, index) => {
    units.slice(index + 1).forEach(other => {
      if (unit.blockId === other.blockId || !unitsOverlap(unit, other)) {
        return;
      }

      conflicts.push({
        blockId: unit.blockId,
        otherBlockId: other.blockId,
        dayOfWeek: unit.dayOfWeek,
      });
    });
  });

  return conflicts;
}

export function hasCoverageConflict(blocks: CoverageBlockInput[]): boolean {
  return findCoverageConflicts(blocks).length > 0;
}
